import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  FileText,
  Check,
  X,
  ChevronLeft,
  AlertCircle,
  Download,
  Loader2,
  CheckCircle2,
  User,
  MapPin,
  Calendar,
  Filter,
  Search,
  Send,
  Eye,
  Pen
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { SignaturePad } from '../../components/ui/signature-pad';
import { toast } from 'sonner';
import api from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Timesheet Review Dashboard
 * 
 * For Office Staff (Cat, Jayke, Anessa) to:
 * - View submitted timesheets pending review
 * - Add counter-signature for approval
 * - Reject with reason
 * - Download PDF for Payroll/TriWest
 */

const STATUS_CONFIG = {
  submitted: { 
    color: 'bg-blue-100 text-blue-800 border-blue-200', 
    label: 'Pending Review',
    icon: Clock
  },
  under_review: { 
    color: 'bg-amber-100 text-amber-800 border-amber-200', 
    label: 'Under Review',
    icon: Eye
  },
  approved: { 
    color: 'bg-green-100 text-green-800 border-green-200', 
    label: 'Approved',
    icon: CheckCircle2
  },
  payroll_approved: { 
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200', 
    label: 'Payroll Approved',
    icon: CheckCircle2
  },
  rejected: { 
    color: 'bg-red-100 text-red-800 border-red-200', 
    label: 'Rejected',
    icon: X
  },
};

export default function TimesheetReviewDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [approvedTimesheets, setApprovedTimesheets] = useState([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [careNotes, setCareNotes] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Approval modal
  const [showApproval, setShowApproval] = useState(false);
  const [approvalType, setApprovalType] = useState('supervisor'); // 'supervisor' or 'payroll'
  
  // Rejection modal
  const [showRejection, setShowRejection] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  useEffect(() => {
    fetchTimesheets();
  }, []);
  
  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      
      // Fetch pending review
      const pendingRes = await api.get('/timesheets/pending-review');
      setPendingTimesheets(pendingRes?.items || []);
      
      // Fetch approved (ready for payroll)
      const approvedRes = await api.get('/timesheets/pending-payroll');
      setApprovedTimesheets(approvedRes?.items || []);
      
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTimesheetDetails = async (timesheet) => {
    try {
      const res = await api.get(`/timesheets/${timesheet.id}`);
      setSelectedTimesheet(res.timesheet);
      setCareNotes(res.care_notes || []);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Failed to load timesheet details');
    }
  };
  
  const handleApprove = async (signatureData) => {
    if (!selectedTimesheet) return;
    
    try {
      await api.post('/timesheets/approve', {
        timesheet_id: selectedTimesheet.id,
        signature: signatureData,
        approval_type: approvalType,
      });
      
      toast.success(
        approvalType === 'supervisor' 
          ? 'Timesheet approved! Ready for payroll processing.'
          : 'Timesheet approved for payroll!'
      );
      
      setShowApproval(false);
      setSelectedTimesheet(null);
      fetchTimesheets();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(error?.detail || 'Failed to approve timesheet');
    }
  };
  
  const handleReject = async () => {
    if (!selectedTimesheet || !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      await api.post('/timesheets/reject', {
        timesheet_id: selectedTimesheet.id,
        reason: rejectionReason,
      });
      
      toast.success('Timesheet returned to caregiver for revision');
      setShowRejection(false);
      setRejectionReason('');
      setSelectedTimesheet(null);
      fetchTimesheets();
    } catch (error) {
      console.error('Rejection error:', error);
      toast.error('Failed to reject timesheet');
    }
  };
  
  const downloadPdf = (timesheetId) => {
    window.open(`${API_URL}/api/timesheets/${timesheetId}/pdf`, '_blank');
  };
  
  // Filter timesheets by search
  const filteredPending = pendingTimesheets.filter(ts => 
    ts.caregiver_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ts.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredApproved = approvedTimesheets.filter(ts => 
    ts.caregiver_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ts.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#176DFF] animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B33]">Timesheet Review</h1>
          <p className="text-gray-500">Review and approve submitted timesheets</p>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0D1B33]">{pendingTimesheets.length}</p>
                <p className="text-sm text-gray-500">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0D1B33]">{approvedTimesheets.length}</p>
                <p className="text-sm text-gray-500">Ready for Payroll</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0D1B33]">
                  {pendingTimesheets.reduce((sum, ts) => sum + (ts.total_hours || 0), 0).toFixed(1)}
                </p>
                <p className="text-sm text-gray-500">Hours Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs & Search */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'pending'
                ? 'bg-[#176DFF] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Pending Review ({pendingTimesheets.length})
          </button>
          <button
            onClick={() => setActiveTab('payroll')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'payroll'
                ? 'bg-[#176DFF] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Ready for Payroll ({approvedTimesheets.length})
          </button>
        </div>
        
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      {/* Timesheet List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: List */}
        <div className="space-y-3">
          {(activeTab === 'pending' ? filteredPending : filteredApproved).map((ts) => {
            const status = STATUS_CONFIG[ts.status] || STATUS_CONFIG.submitted;
            const StatusIcon = status.icon;
            
            return (
              <Card 
                key={ts.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedTimesheet?.id === ts.id ? 'ring-2 ring-[#176DFF]' : ''
                }`}
                onClick={() => fetchTimesheetDetails(ts)}
                data-testid={`timesheet-card-${ts.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-[#0D1B33]">{ts.caregiver_name}</h3>
                      <p className="text-sm text-gray-500">{ts.client_name}</p>
                    </div>
                    <Badge className={status.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {status.label}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {ts.pay_period_start} - {ts.pay_period_end}
                    </span>
                    <span className="font-medium text-[#176DFF]">
                      {ts.total_hours} hrs
                    </span>
                  </div>
                  
                  {ts.submitted_at && (
                    <p className="text-xs text-gray-400 mt-2">
                      Submitted: {new Date(ts.submitted_at).toLocaleDateString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
          
          {(activeTab === 'pending' ? filteredPending : filteredApproved).length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">
                  {activeTab === 'pending' 
                    ? 'No timesheets pending review'
                    : 'No timesheets ready for payroll'
                  }
                </p>
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Right: Detail View */}
        <div>
          {selectedTimesheet ? (
            <Card className="sticky top-4">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Timesheet Details</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdf(selectedTimesheet.id)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Caregiver & Client Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Caregiver</p>
                    <p className="font-medium">{selectedTimesheet.caregiver_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Client</p>
                    <p className="font-medium">{selectedTimesheet.client_name}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500">Client Address</p>
                  <p className="font-medium">{selectedTimesheet.client_address}</p>
                </div>
                
                {/* Hours Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Pay Period</p>
                      <p className="font-medium">
                        {selectedTimesheet.pay_period_start} - {selectedTimesheet.pay_period_end}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Total Hours</p>
                      <p className="text-2xl font-bold text-[#176DFF]">
                        {selectedTimesheet.total_hours}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Time Entries */}
                <div>
                  <h4 className="font-medium mb-2">Time Entries ({selectedTimesheet.total_entries})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedTimesheet.entries?.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <p className="text-sm font-medium">{entry.service_date}</p>
                          <p className="text-xs text-gray-500">
                            {entry.time_in} - {entry.time_out}
                          </p>
                        </div>
                        <span className="font-medium text-[#176DFF]">{entry.hours_worked} hrs</span>
                      </div>
                    ))}
                    
                    {(!selectedTimesheet.entries || selectedTimesheet.entries.length === 0) && (
                      <p className="text-sm text-gray-500 text-center py-4">No entries</p>
                    )}
                  </div>
                </div>
                
                {/* Care Notes Summary */}
                <div>
                  <h4 className="font-medium mb-2">Care Notes</h4>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      {selectedTimesheet.care_notes_completed || 0} / {selectedTimesheet.care_notes_required || 0} Complete
                    </Badge>
                    {selectedTimesheet.all_care_notes_complete && (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    )}
                  </div>
                </div>
                
                {/* Caregiver Signature */}
                {selectedTimesheet.caregiver_signature && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Pen className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Caregiver Signature</span>
                    </div>
                    <p className="text-sm text-blue-700">
                      Signed by {selectedTimesheet.caregiver_signature.signer_name}
                    </p>
                    <p className="text-xs text-blue-600">
                      {new Date(selectedTimesheet.caregiver_signature.signed_at).toLocaleString()}
                    </p>
                  </div>
                )}
                
                {/* Action Buttons */}
                {selectedTimesheet.status === 'submitted' && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => setShowRejection(true)}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Return for Revision
                    </Button>
                    <Button
                      className="flex-1 bg-[#176DFF] hover:bg-[#176DFF]/90"
                      onClick={() => {
                        setApprovalType('supervisor');
                        setShowApproval(true);
                      }}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  </div>
                )}
                
                {selectedTimesheet.status === 'approved' && (
                  <div className="pt-4 border-t">
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        setApprovalType('payroll');
                        setShowApproval(true);
                      }}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve for Payroll
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Eye className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">Select a timesheet to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Approval Modal */}
      {showApproval && selectedTimesheet && (
        <SignaturePad
          signerName={user?.name}
          acknowledgmentText={
            approvalType === 'supervisor'
              ? "I have reviewed this timesheet and verify that the hours and services documented are accurate. I approve this timesheet for payroll processing."
              : "I approve this timesheet for payroll processing. The caregiver will be compensated for the hours documented."
          }
          submitLabel={
            approvalType === 'supervisor' 
              ? 'Sign & Approve Timesheet'
              : 'Sign & Approve for Payroll'
          }
          onSign={handleApprove}
          onCancel={() => setShowApproval(false)}
        />
      )}
      
      {/* Rejection Modal */}
      {showRejection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-[#0D1B33]">Return for Revision</h3>
              <button onClick={() => setShowRejection(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for returning this timesheet. The caregiver will be notified and can make corrections.
              </p>
              
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for revision..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                rows={4}
              />
              
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowRejection(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleReject}
                  disabled={!rejectionReason.trim()}
                >
                  Return Timesheet
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
